import React, { useCallback } from 'react';
import { ComponentInfo, MembershipCompletionTitle, SummaryNextSteps } from '@components';
import {
	Box,
	ComposableTypography,
	Spacer,
	Typography,
	Image,
	Button,
} from '@bcp-nextgen-dx-component-factory/accolade-design-system';
import { rem } from '@helpers';
import { imagesBcp } from '@assets';
import { useSelectedPlan, useServiceResponse, useUserInput } from '@store';
import { TransactionStatus } from '@api/izibizi-xp';
import { useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { useNavigation } from '@navigation';

type SubscriptionStatus = 'success' | 'pending';

export const MembershipCompletionTitlePlaceholder: React.FC = () => {
	const { subscriptionMaintenance } = useServiceResponse();
	const { exit } = useNavigation();
	const t = useTranslation();
	const { email } = useUserInput();
	const selectedPlan = useSelectedPlan();
	const status: SubscriptionStatus =
		subscriptionMaintenance?.transactionStatus === TransactionStatus.OK ? 'success' : 'pending';

	let image = imagesBcp.selectedPlanStartedConclusion;
	if (selectedPlan?.id === 'premium') {
		image = imagesBcp.selectedPlanTotalConclusion;
	}

	const onHandleLeave = useCallback(() => {
		exit();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<ComponentInfo name="ConclusionPlaceholder">
			{status === 'pending' ? (
				<>
					<MembershipCompletionTitle />
					<SummaryNextSteps />
				</>
			) : (
				<>
					<Box styles={{ flexGrow: 1 }}></Box>
					<Box
						styles={{
							flexDirection: 'column',
							backgroundColor: '#FFFFFF',
							borderRadius: rem(16),
							height: rem(416),
							width: '100%',
							padding: rem(176, 32, 40),
						}}
					>
						<Box styles={{ flexDirection: 'column', width: '100%', position: 'relative' }}>
							<Box
								styles={{
									position: 'absolute',
									top: 0,
									left: '50%',
									transform: 'translateX(-60%) translateY(-100%)',
								}}
							>
								<Image source={image} />
							</Box>
							<Box styles={{ flexDirection: 'column', alignItems: 'center' }}>
								<Typography appearance="headline-200-02">
									{t('placeholder.conclusion.success.titleOne')}
								</Typography>
								<Typography appearance="subtitle-100-04">
									{t('placeholder.conclusion.success.titleTwo')}
								</Typography>
							</Box>
							<Spacer vertical={rem(40)} />
							<Box
								styles={{
									flexDirection: 'column',
									backgroundColor: '#F5F6FA',
									borderRadius: rem(16),
									padding: rem(16, 16, 24),
									alignItems: 'center',
								}}
							>
								<Typography appearance="subtitle-100-04">
									{t('placeholder.conclusion.success.instructions.title')}
								</Typography>
								<Spacer vertical={8} />
								<ComposableTypography
									text={t('placeholder.conclusion.success.instructions.description', {
										email: email,
									})}
									baseProps={{
										appearance: 'body-100-02',
									}}
									tags={{
										b: { appearance: 'body-100-04' },
									}}
								/>
							</Box>
						</Box>
					</Box>
				</>
			)}
			<Spacer vertical={rem(56)} />
			<Box styles={{ justifyContent: 'center' }}>
				<Button variant="light" appearance="secondary" size="medium" onPress={onHandleLeave}>
					{t('general.backToHomepage')}
				</Button>
			</Box>
		</ComponentInfo>
	);
};
